import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { type Column, StandardTable } from '../../components/common/UI';
import { useAccount } from '../../contexts/AccountContext';
import { PERMISSIONS } from '../../shared/constants/permissions';
import type { Tender } from '../../types/tender';

interface ArchiveTableProps {
  tenders: Tender[];
  isLoading: boolean;
  usersMap: Record<string, { displayName: string; teamId?: string }>;
  onRowClick?: (tender: Tender) => void;
}

type Order = 'asc' | 'desc';
type SortKey =
  | 'archivedDate'
  | 'ownerId'
  | 'title'
  | 'tenderIssuer'
  | 'state'
  | 'bidCost'
  | 'tenderValue';

const formatCurrency = (amount?: number) => {
  if (amount === undefined || amount === null) {
    return '-';
  }
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0,
  }).format(amount);
};

// Handle both Firestore Timestamps and strings
const formatDate = (dateValue: string | Date | { toDate: () => Date } | null | undefined) => {
  if (!dateValue) {
    return '-';
  }
  const date =
    dateValue && typeof dateValue === 'object' && 'toDate' in dateValue
      ? (dateValue as { toDate: () => Date }).toDate()
      : new Date(dateValue as string | Date);
  return new Intl.DateTimeFormat('en-AU', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
};

const ArchiveTable: React.FC<ArchiveTableProps> = ({
  tenders,
  isLoading,
  usersMap,
  onRowClick,
}) => {
  const theme = useTheme();
  const { account } = useAccount();

  const [filteredRows, setFilteredRows] = useState<Tender[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [stateFilter, setStateFilter] = useState<string[]>([]);
  const [filterOwner, setFilterOwner] = useState<'all' | 'mine'>('all');
  const [selectedTeam, setSelectedTeam] = useState<string>('all');
  const [selectedUser, setSelectedUser] = useState<string>('all');

  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<SortKey>('archivedDate');

  const canViewAll = account?.cachedPermissions?.includes(PERMISSIONS.TENDER_VIEW_CLIENT);
  const canManageTeam = account?.cachedPermissions?.includes(PERMISSIONS.TENDER_MANAGE_TEAM);
  const allLabel = !canViewAll && canManageTeam ? 'Team Tenders' : 'All Tenders';

  const relevantUserIds = useMemo(() => {
    const ids = new Set<string>();
    for (const t of tenders) {
      if (t.ownerId) {
        ids.add(t.ownerId);
      }
    }
    return ids;
  }, [tenders]);

  const relevantTeamIds = useMemo(() => {
    const ids = new Set<string>();
    for (const t of tenders) {
      const owner = usersMap[t.ownerId || ''];
      if (owner?.teamId) {
        ids.add(owner.teamId);
      }
    }
    return ids;
  }, [tenders, usersMap]);

  useEffect(() => {
    let newFiltered = [...tenders];

    if (filterOwner === 'mine' && account?.uid) {
      newFiltered = newFiltered.filter((row) => row.ownerId === account.uid);
    } else if (filterOwner === 'all') {
      if (selectedTeam !== 'all') {
        newFiltered = newFiltered.filter((row) => {
          const ownerTeamId = usersMap?.[row.ownerId || '']?.teamId;
          return ownerTeamId === selectedTeam;
        });
      }
      if (selectedUser !== 'all') {
        newFiltered = newFiltered.filter((row) => row.ownerId === selectedUser);
      }
    }

    if (stateFilter.length > 0) {
      newFiltered = newFiltered.filter((tender) => {
        const tState = (tender.state || tender.location || '').toUpperCase();
        return stateFilter.some((s) => tState.includes(s)) || tState.includes('NATIONAL');
      });
    }

    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      newFiltered = newFiltered.filter(
        (row) =>
          row.title?.toLowerCase().includes(lowerSearch) ||
          row.tenderIssuer?.toLowerCase().includes(lowerSearch) ||
          row.state?.toLowerCase().includes(lowerSearch) ||
          row.location?.toLowerCase().includes(lowerSearch) ||
          (row.keywords &&
            Array.isArray(row.keywords) &&
            row.keywords.some((kw: string) => kw.toLowerCase().includes(lowerSearch)))
      );
    }

    newFiltered.sort((a, b) => {
      let valA: string | number = 0;
      let valB: string | number = 0;

      if (orderBy === 'archivedDate') {
        const dateA = a.archivedDate || a.updatedAt || 0;
        const dateB = b.archivedDate || b.updatedAt || 0;
        valA =
          dateA && typeof dateA === 'object' && 'toMillis' in dateA
            ? (dateA as { toMillis: () => number }).toMillis()
            : new Date(dateA as string | Date).getTime();
        valB =
          dateB && typeof dateB === 'object' && 'toMillis' in dateB
            ? (dateB as { toMillis: () => number }).toMillis()
            : new Date(dateB as string | Date).getTime();
      } else if (orderBy === 'ownerId') {
        valA = (a.ownerId ? usersMap[a.ownerId]?.displayName || a.ownerId : '').toLowerCase();
        valB = (b.ownerId ? usersMap[b.ownerId]?.displayName || b.ownerId : '').toLowerCase();
      } else {
        const vA = a[orderBy];
        const vB = b[orderBy];
        valA = typeof vA === 'string' ? vA.toLowerCase() : vA || 0;
        valB = typeof vB === 'string' ? vB.toLowerCase() : vB || 0;
      }

      if (valA < valB) {
        return order === 'asc' ? -1 : 1;
      }
      if (valA > valB) {
        return order === 'asc' ? 1 : -1;
      }
      return 0;
    });

    setFilteredRows(newFiltered);
  }, [
    tenders,
    filterOwner,
    selectedTeam,
    selectedUser,
    stateFilter,
    searchTerm,
    order,
    orderBy,
    account?.uid,
    usersMap,
  ]);

  const handleRequestSort = (property: SortKey) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const createSortHandler = (property: SortKey) => () => {
    handleRequestSort(property);
  };

  const renderSortableLabel = (id: SortKey, label: string) => (
    <TableSortLabel
      active={orderBy === id}
      direction={orderBy === id ? order : 'asc'}
      onClick={createSortHandler(id)}
    >
      {label}
    </TableSortLabel>
  );

  const columns: Column<Tender>[] = [
    {
      id: 'archivedDate',
      label: renderSortableLabel('archivedDate', 'Date Archived'),
      render: (tender) => formatDate(tender.archivedDate || tender.updatedAt),
    },
    {
      id: 'ownerId',
      label: renderSortableLabel('ownerId', 'Tender Owner'),
      render: (tender) =>
        tender.ownerId && usersMap[tender.ownerId]
          ? usersMap[tender.ownerId]?.displayName
          : tender.ownerId || '-',
    },
    {
      id: 'title',
      label: renderSortableLabel('title', 'Tender Title'),
      render: (tender) => (
        <Box>
          <Typography variant="subtitle2" noWrap sx={{ maxWidth: 300 }}>
            {tender.title || 'Untitled Tender'}
          </Typography>
          {tender.bidType && (
            <Chip
              size="small"
              label={tender.bidType}
              sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }}
            />
          )}
        </Box>
      ),
    },
    {
      id: 'tenderIssuer',
      label: renderSortableLabel('tenderIssuer', 'Issuer'),
      render: (tender) => tender.tenderIssuer || tender.companyId || '-',
    },
    {
      id: 'state',
      label: renderSortableLabel('state', 'State'),
      render: (tender) => tender.state || '-',
    },
    {
      id: 'bidCost',
      label: renderSortableLabel('bidCost', 'Bid Cost'),
      align: 'right',
      render: (tender) => formatCurrency(tender.bidCost),
    },
    {
      id: 'tenderValue',
      label: renderSortableLabel('tenderValue', 'Tender Value'),
      align: 'right',
      render: (tender) => formatCurrency(tender.tenderValue),
    },
  ];

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Filtering Toolbar */}
      <Grid container spacing={2} sx={{ mb: 2, alignItems: 'center' }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search tenders..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              backgroundColor: theme.palette.greyLight,
              borderRadius: 3,
              '& fieldset': { borderRadius: 3, borderColor: theme.palette.text.primary },
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl variant="outlined" size="small" sx={{ borderRadius: 3 }} fullWidth>
            <InputLabel id="region-filter-label">Filter by Region</InputLabel>
            <Select
              labelId="region-filter-label"
              id="region-filter-select"
              multiple
              value={stateFilter}
              onChange={(e) =>
                setStateFilter(
                  typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value
                )
              }
              label="Filter by Region"
              renderValue={(selected) =>
                selected.length === 0 ? (
                  <Typography variant="body2">All Regions</Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )
              }
            >
              {['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'].map((state) => (
                <MenuItem key={state} value={state}>
                  <Checkbox checked={stateFilter.includes(state)} size="small" />
                  <ListItemText primary={state} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        {(canViewAll || canManageTeam) && (
          <Grid size={{ xs: 12 }} container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Ownership</InputLabel>
                <Select
                  value={filterOwner}
                  label="Ownership"
                  onChange={(e) => setFilterOwner(e.target.value as 'all' | 'mine')}
                >
                  <MenuItem value="all">{allLabel}</MenuItem>
                  <MenuItem value="mine">My Tenders</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl size="small" fullWidth disabled={filterOwner === 'mine'}>
                <InputLabel>Team</InputLabel>
                <Select
                  value={selectedTeam}
                  label="Team"
                  onChange={(e) => {
                    setSelectedTeam(e.target.value);
                    setSelectedUser('all');
                  }}
                >
                  <MenuItem value="all">All Teams</MenuItem>
                  {Array.from(relevantTeamIds).map((teamId) => (
                    <MenuItem key={teamId} value={teamId}>
                      {teamId}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl size="small" fullWidth disabled={filterOwner === 'mine'}>
                <InputLabel>User</InputLabel>
                <Select
                  value={selectedUser}
                  label="User"
                  onChange={(e) => setSelectedUser(e.target.value)}
                >
                  <MenuItem value="all">All Users</MenuItem>
                  {Array.from(relevantUserIds)
                    .filter((id) => selectedTeam === 'all' || usersMap[id]?.teamId === selectedTeam)
                    .map((id) => (
                      <MenuItem key={id} value={id}>
                        {usersMap[id]?.displayName || id}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        )}
      </Grid>
      {!filteredRows.length ? (
        <Paper sx={{ p: 4, textAlign: 'center', mt: 2 }}>
          <Typography variant="body1" color="text.secondary">
            No archived tenders found.
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 1, px: 1 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <Box sx={{ width: 4, height: 12, backgroundColor: '#4caf50' }} /> Included in Metrics
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <Box sx={{ width: 4, height: 12, backgroundColor: theme.palette.error.main }} />{' '}
              Excluded
            </Typography>
          </Box>
          <StandardTable
            columns={columns}
            data={filteredRows}
            keyExtractor={(tender) => tender.id || tender.tenderId || '0'}
            onRowClick={onRowClick}
            size="small"
            rowSx={(tender) => ({
              '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover },
              borderLeft:
                tender.includeInMetrics !== false
                  ? '4px solid #4caf50'
                  : `4px solid ${theme.palette.error.main}`,
            })}
          />
        </Box>
      )}{' '}
    </Box>
  );
};

export default ArchiveTable;
